import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  useTheme,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileServices from "services/FileServices";
import { getEnv } from "envConfig";
import { IMAGE_UPLOAD_PATH } from "apiRoutes";

// Check if browser supports WebP
const supportsWebP = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL("image/webp").indexOf("image/webp") === 5;
};

const normalizeImagePath = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return normalizeImagePath(value[0]);
  if (typeof value === "object") {
    return normalizeImagePath(value.path || value.url || value.filename || value.name);
  }
  const path = String(value).trim();
  return path || null;
};

const isDirectUrl = (path) =>
  /^(https?:|blob:|data:)/i.test(path);

const buildPublicImageUrl = (path) => {
  if (isDirectUrl(path)) return path;
  const env = getEnv();
  const clean = path.replace(/^\/+/, "");
  if (clean.startsWith("api/restaurant/uploads/")) {
    return `${env.ENDPOINT}/${clean}`;
  }
  return `${env.ENDPOINT}${IMAGE_UPLOAD_PATH}/${clean}`;
};

/**
 * ImageLoader - Component for loading and displaying images from API
 * @param {string} imagePath - Relative path from data (e.g., "categories/uuid/filename.jpg")
 * @param {number|string} width - Image width (default: 100)
 * @param {number|string} height - Image height (default: 100)
 * @param {string} alt - Alt text for image (default: "Image")
 * @param {boolean} rounded - Add border radius (default: true)
 * @param {number} borderRadius - Border radius value (default: 1)
 */
const ImageLoader = ({
  imagePath,
  width = 100,
  height = 100,
  alt = "Image",
  rounded = true,
  borderRadius = 1,
}) => {
  const theme = useTheme();
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const resolvedPath = normalizeImagePath(imagePath);
    if (!resolvedPath) {
      setLoading(false);
      setError(true);
      return;
    }

    let isMounted = true;
    let blobUrlToCleanup = null;

    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        if (isDirectUrl(resolvedPath)) {
          if (isMounted) {
            setImageUrl(resolvedPath);
            setLoading(false);
          }
          return;
        }

        if (resolvedPath.toLowerCase().endsWith(".webp") && !supportsWebP()) {
          throw new Error("WebP format not supported by browser");
        }

        try {
          const blob = await FileServices.getImageBlob(resolvedPath);
          if (!isMounted) return;

          if (!blob) {
            throw new Error("Empty image response");
          }

          if (blob.type && !blob.type.startsWith("image/") && !resolvedPath.toLowerCase().endsWith(".webp")) {
            throw new Error(`Invalid image type: ${blob.type}`);
          }

          const blobUrl = URL.createObjectURL(
            blob.type?.startsWith("image/") ? blob : new Blob([blob], { type: "image/webp" }),
          );
          blobUrlToCleanup = blobUrl;

          const testImage = new Image();
          testImage.onload = () => {
            if (isMounted) {
              setImageUrl(blobUrl);
              setLoading(false);
            }
          };
          testImage.onerror = () => {
            if (!isMounted) return;
            URL.revokeObjectURL(blobUrl);
            blobUrlToCleanup = null;
            setImageUrl(buildPublicImageUrl(resolvedPath));
            setLoading(false);
          };
          testImage.src = blobUrl;
          return;
        } catch {
          if (!isMounted) return;
          setImageUrl(buildPublicImageUrl(resolvedPath));
          setLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Error loading image:", err);
        setError(true);
        setLoading(false);
      }
    };

    loadImage();

    return () => {
      isMounted = false;
      if (blobUrlToCleanup) {
        URL.revokeObjectURL(blobUrlToCleanup);
      }
    };
  }, [imagePath]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          width: width,
          height: height,
          position: "relative",
          borderRadius: rounded ? borderRadius : 0,
          overflow: "hidden",
          background: `linear-gradient(
            90deg,
            ${theme.palette.action.hover} 0%,
            ${theme.palette.action.disabled} 50%,
            ${theme.palette.action.hover} 100%
          )`,
          backgroundSize: "200% 100%",
          animation: "imagePulse 2s infinite",
          "@keyframes imagePulse": {
            "0%": {
              backgroundPosition: "0% 50%",
            },
            "50%": {
              backgroundPosition: "100% 50%",
            },
            "100%": {
              backgroundPosition: "0% 50%",
            },
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress
          size={typeof width === "number" ? width / 3 : 40}
          thickness={4}
          sx={{
            color: theme.palette.primary.main,
            opacity: 0.7,
          }}
        />
      </Box>
    );
  }

  // Error state
  if (error || !imageUrl) {
    return (
      <Box
        sx={{
          width: width,
          height: height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
          borderRadius: rounded ? borderRadius : 0,
          border: "1px solid #e0e0e0",
          gap: 1,
        }}
      >
        <ErrorOutlineIcon sx={{ color: "#999", fontSize: 24 }} />
      </Box>
    );
  }

  // Success state - display image
  return (
    <Box
      component="img"
      src={imageUrl}
      alt={alt}
      onError={() => {
        setError(true);
        setImageUrl(null);
      }}
      sx={{
        width: width,
        height: height,
        objectFit: "cover",
        borderRadius: rounded ? borderRadius : 0,
        display: "block",
        animation: "fadeIn 0.5s ease-in-out",
        "@keyframes fadeIn": {
          "0%": {
            opacity: 0,
          },
          "100%": {
            opacity: 1,
          },
        },
      }}
    />
  );
};

export default ImageLoader;
